import React from 'react';
import PageHero from '../components/PageHero';
import { heroImages, serviceSegments, brand } from '../mock';
import * as Icons from 'lucide-react';
import { ArrowRight, CheckCircle2, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const detailed = [
  {
    id: 'ecommerce',
    color: '#1F8B4C',
    img: heroImages.warehouse,
    features: [
      'Seller Central account setup & verification',
      'Product listing with SEO-optimized content',
      'Catalog & inventory management',
      'A+ Content and brand store design',
      'PPC advertising & campaign optimization',
      'Returns, disputes & customer support',
    ],
  },
  {
    id: 'social',
    color: '#FF7A00',
    img: heroImages.social,
    features: [
      'Instagram & Facebook page management',
      'Content calendar & post creation',
      'Reels, short videos & carousels',
      'Meta Ads campaign management',
      'Influencer collaborations',
      'Community moderation & analytics',
    ],
  },
  {
    id: 'web',
    color: '#146638',
    img: heroImages.coding,
    features: [
      'Custom website & landing page design',
      'Shopify & WooCommerce stores',
      'Payment gateway & shipping integration',
      'On-page SEO optimization',
      'Mobile-first responsive design',
      'Hosting, maintenance & support',
    ],
  },
];

export default function ServicesPage() {
  return (
    <>
      <PageHero
        eyebrow="Our Services"
        title="Everything You Need. Under One Roof."
        sub="E-Commerce marketplace management, Social Media growth and Website Development — a diversified suite of services built to scale your brand."
        image={heroImages.product}
        accent="orange"
      />

      <section className="section bg-white">
        <div className="container-x space-y-24">
          {serviceSegments.map((seg, i) => {
            const Icon = Icons[seg.icon] || Icons.Sparkles;
            const d = detailed.find((x) => x.id === seg.id);
            const reverse = i % 2 === 1;
            return (
              <div
                key={seg.id}
                id={seg.id}
                className={`grid lg:grid-cols-2 gap-10 lg:gap-14 items-center ${reverse ? 'lg:[direction:rtl]' : ''}`}
              >
                <div className="[direction:ltr] relative">
                  <img src={d.img} alt={seg.title} className="rounded-3xl w-full h-[420px] object-cover shadow-2xl border-4 border-white" />
                  <div
                    className="absolute -bottom-6 left-6 rounded-2xl px-5 py-4 shadow-xl flex items-center gap-3"
                    style={{ background: d.color, color: '#fff' }}
                  >
                    <Icon size={22} />
                    <div className="font-black uppercase text-xs tracking-widest">{seg.subtitle}</div>
                  </div>
                </div>
                <div className="[direction:ltr]">
                  <div className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
                       style={{ background: `${d.color}18`, color: d.color }}>
                    Service 0{i + 1}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-black text-[color:var(--ink)] leading-tight">{seg.title}</h2>
                  <p className="section-sub mt-4">{seg.desc}</p>

                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    {d.features.map((f) => (
                      <div key={f} className="flex items-start gap-2 text-sm text-[color:var(--ink)]">
                        <CheckCircle2 size={18} style={{ color: d.color }} className="mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex flex-wrap gap-2">
                    {seg.items.map((it) => (
                      <span key={it} className="px-3 py-1.5 rounded-full text-xs font-semibold border"
                            style={{ borderColor: `${d.color}55`, color: d.color, background: `${d.color}0d` }}>
                        {it}
                      </span>
                    ))}
                  </div>

                  <Link to="/contact" className="btn-primary mt-7">
                    Get Started <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-[color:var(--brand-orange)] to-[#FF9F42] text-white text-center">
        <div className="container-x">
          <h2 className="text-3xl md:text-4xl font-black">Not sure which service you need?</h2>
          <p className="mt-3 text-white/90">Let our experts audit your business for free and recommend the right plan.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href={`https://wa.me/${brand.whatsapp}?text=${encodeURIComponent('Hi EC Safetrade, I would like a FREE business audit. Please share more details.')}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 font-semibold px-7 py-3 rounded-full bg-white text-[color:var(--brand-orange)] shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition"
            >
              <MessageCircle size={18} /> Request Free Audit on WhatsApp
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 font-semibold px-7 py-3 rounded-full border-2 border-white text-white hover:bg-white/10 transition"
            >
              Or fill the contact form <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
